import { ATTENTION_QUEUE_QUERY, RESUME_PROGRAMME_QUERY } from "@lib/graphql/outreach";
import { useOutreachListChannel } from "@lib/hooks/useOutreachListChannel";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useCallback } from "react";
import { useMutation, useQuery } from "urql";
import type { AttentionQueue } from "./types";

export function AttentionTab() {
  const [{ data, fetching }, reexecute] = useQuery({ query: ATTENTION_QUEUE_QUERY });
  const [, resumeQuery] = useMutation(RESUME_PROGRAMME_QUERY);
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useOutreachListChannel(refresh);

  const attention: AttentionQueue | null = data?.attentionQueue ?? null;

  if (fetching && !attention) {
    return <p className="text-sm text-text-muted">Loading attention queue…</p>;
  }

  const nothingToDo =
    attention &&
    attention.failedProgrammeQueries.length === 0 &&
    attention.failedBuyers.length === 0 &&
    attention.staleUnresolvedPostings.length === 0;

  if (!attention || nothingToDo) {
    return (
      <div className="rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
        <p className="text-sm text-text-muted">Nothing needs attention right now.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {attention.failedProgrammeQueries.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-text-primary">Failed programme queries</h3>
          <ul className="mt-2 divide-y divide-border overflow-hidden rounded-md border border-border">
            {attention.failedProgrammeQueries.map((q) => (
              <li key={q.id} className="flex items-start justify-between gap-3 px-3 py-2 text-sm">
                <div>
                  <p className="font-medium text-text-primary">{q.name}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                    <AlertTriangle size={12} /> {q.lastError || "Unknown error"}
                  </p>
                </div>
                <button
                  onClick={async () => {
                    await resumeQuery({ input: { id: q.id } });
                    refresh();
                  }}
                  className="flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover"
                >
                  <RefreshCw size={12} /> Retry
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {attention.failedBuyers.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-text-primary">Failed buyer imports</h3>
          <ul className="mt-2 divide-y divide-border overflow-hidden rounded-md border border-border">
            {attention.failedBuyers.map((b) => (
              <li key={b.id} className="px-3 py-2 text-sm">
                <p className="font-medium text-text-primary">{b.fullName ?? b.linkedinUrl}</p>
                <p className="mt-0.5 text-xs text-text-muted">
                  {b.prospectCompany?.name ?? "Unknown company"}
                </p>
                {b.lastError && (
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                    <AlertTriangle size={12} /> {b.lastError}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {attention.staleUnresolvedPostings.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-text-primary">
            Postings with unresolved hiring company
          </h3>
          <ul className="mt-2 divide-y divide-border overflow-hidden rounded-md border border-border">
            {attention.staleUnresolvedPostings.map((p) => (
              <li key={p.id} className="px-3 py-2 text-sm">
                <p className="font-medium text-text-primary">{p.title ?? "Untitled"}</p>
                <p className="mt-0.5 text-xs text-text-muted">
                  Posted by {p.posterName ?? "—"}
                  {p.posterCompany ? ` · ${p.posterCompany}` : ""}
                  {` · signal ${p.agencyLikelihoodScore ?? "—"}/100`}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
